import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatLongDate, todayISO } from "@/lib/format";
import { AdminButton, AdminCard, AdminField, EmptyState, adminInput } from "@/components/admin/ui";

const TYPES = [
  ["closed", "Chiusura"],
  ["extra_opening", "Apertura straordinaria"],
  ["modified", "Servizio modificato"],
  ["event", "Evento"],
];

export default function AdminSpecialDates() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [form, setForm] = useState({
    date: todayISO(),
    type: "closed",
    reason: "",
    online_booking_enabled: false,
    override: false,
    svc_name: "Cena",
    svc_start: "19:45",
    svc_end: "23:00",
    svc_interval: 30,
    svc_capacity: 18,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/special-dates");
      setItems(data);
    } catch (err) {
      setError(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setError("");
    setOk("");
    const payload = {
      date: form.date,
      type: form.type,
      reason: form.reason,
      online_booking_enabled: form.type === "closed" ? false : !!form.online_booking_enabled,
      services_override:
        form.type === "closed" || !form.override
          ? []
          : [
              {
                weekday: new Date(form.date).getDay() === 0 ? 6 : new Date(form.date).getDay() - 1,
                name: form.svc_name,
                start_time: form.svc_start,
                end_time: form.svc_end,
                slot_interval_minutes: Number(form.svc_interval),
                last_seating_offset_minutes: 90,
                capacity: Number(form.svc_capacity),
                max_online_party_size: 8,
                online_booking_enabled: !!form.online_booking_enabled,
                active: true,
              },
            ],
    };
    try {
      await api.post("/admin/special-dates", payload);
      setForm({ ...form, reason: "" });
      setOk(`Data speciale salvata per ${formatLongDate(form.date)}.`);
      window.setTimeout(() => setOk(""), 6000);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/special-dates/${id}`);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-special-dates">
      <div>
        <p className="text-[0.62rem] uppercase tracking-[0.2em] text-sage">Configurazione</p>
        <h1 className="mt-2 font-serif text-3xl md:text-4xl text-wood">Date speciali</h1>
        <p className="mt-3 max-w-2xl text-sm text-foreground/65">
          Chiusure, aperture straordinarie ed eventi. Una data speciale sostituisce la configurazione
          settimanale.
        </p>
      </div>

      {error && <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive" data-testid="special-error">{error}</p>}
      {ok && (
        <p className="border border-sage/50 bg-sage/10 px-4 py-3 text-sm text-wood" data-testid="special-ok">
          {ok}
        </p>
      )}

      <AdminCard title="Nuova data speciale">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <AdminField label="Data">
            <input type="date" value={form.date} onChange={(e) => e.target.value && setForm({ ...form, date: e.target.value })} className={adminInput} data-testid="special-date" />
          </AdminField>
          <AdminField label="Tipo">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={adminInput} data-testid="special-type">
              {TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </AdminField>
          <AdminField label="Motivo / nota" className="sm:col-span-2">
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={adminInput} placeholder="Ferie, Capodanno, evento privato…" data-testid="special-reason" />
          </AdminField>
        </div>

        {form.type !== "closed" && (
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm text-foreground/70">
              <input type="checkbox" checked={form.online_booking_enabled} onChange={(e) => setForm({ ...form, online_booking_enabled: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--sage))]" data-testid="special-online" />
              Prenotazione online attiva in questa data
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground/70">
              <input type="checkbox" checked={form.override} onChange={(e) => setForm({ ...form, override: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--sage))]" data-testid="special-override" />
              Definisci un servizio specifico per questa data
            </label>
            {form.override && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 border border-border p-4">
                <AdminField label="Nome">
                  <input value={form.svc_name} onChange={(e) => setForm({ ...form, svc_name: e.target.value })} className={adminInput} />
                </AdminField>
                <AdminField label="Inizio">
                  <input value={form.svc_start} onChange={(e) => setForm({ ...form, svc_start: e.target.value })} className={adminInput} />
                </AdminField>
                <AdminField label="Fine">
                  <input value={form.svc_end} onChange={(e) => setForm({ ...form, svc_end: e.target.value })} className={adminInput} />
                </AdminField>
                <AdminField label="Intervallo">
                  <input type="number" value={form.svc_interval} onChange={(e) => setForm({ ...form, svc_interval: e.target.value })} className={adminInput} />
                </AdminField>
                <AdminField label="Coperti">
                  <input type="number" value={form.svc_capacity} onChange={(e) => setForm({ ...form, svc_capacity: e.target.value })} className={adminInput} />
                </AdminField>
              </div>
            )}
          </div>
        )}

        <div className="mt-4">
          <AdminButton onClick={save} data-testid="special-save">
            Salva data
          </AdminButton>
        </div>
      </AdminCard>

      <AdminCard title="Date configurate" testId="special-list">
        {items.length === 0 ? (
          <EmptyState title="Nessuna data speciale" description="Aggiungi chiusure o aperture straordinarie." />
        ) : (
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.id} className="flex flex-wrap items-center gap-4 border border-border px-4 py-3" data-testid={`special-${it.date}`}>
                <span className="font-serif text-lg text-wood">{formatLongDate(it.date)}</span>
                <span className="text-[0.62rem] uppercase tracking-[0.16em] text-sage">
                  {TYPES.find(([v]) => v === it.type)?.[1] || it.type}
                </span>
                {it.reason && <span className="text-sm text-foreground/65">{it.reason}</span>}
                {it.type !== "closed" && (
                  <span className="text-xs text-foreground/45">
                    online {it.online_booking_enabled ? "attivo" : "disattivo"}
                    {it.services_override?.length ? ` · ${it.services_override.length} servizio` : ""}
                  </span>
                )}
                <AdminButton variant="danger" className="ml-auto" onClick={() => remove(it.id)} data-testid={`delete-special-${it.date}`}>
                  <Trash2 size={13} />
                </AdminButton>
              </li>
            ))}
          </ul>
        )}
      </AdminCard>
    </div>
  );
}
